import { useMemo } from 'react'
import { ItemQuantityController } from '@/components/ItemQuantityController/ItemQuantityController'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { Service } from '@/models/service'
import { cartSlice } from '@/store/cartSlice'
import { activeElementQuotedSeatsAmountSelector } from '@/store/cartSlice/selectors/activeElementQuotedSeatsAmountSelector'
import { selectActiveCartReservation } from '@/store/cartSlice/selectors/selectActiveCartReservation.ts'
import { selectedElementQuotableProductsQuantitySelector } from '@/store/cartSlice/selectors/selectedElementQuotableProductsQuantitySelector'
import { cartChangeQuotableServiceQuantity } from '@/store/cartSlice/thunks/cartChangeQuotableServiceQuantity.ts'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import { servicesSlice } from '@/store/servicesSlice'
import { toastSlice } from '@/store/toastSlice'
import { SERVICE_LABEL_LIMIT } from '@/utils/constants'

import {
  getItemQuantityLabel,
  ItemQuantityBoundary,
} from '@/utils/getItemQuantityLabel'
import { getServiceLimitReachedMessage } from '@/utils/getServiceLimitReachedMessage'

import { getServiceMinimumQuantity } from '@/utils/getServiceMinimumQuantity'
import { getServiceSingleLimit } from '@/utils/getServiceSingleLimit'

import { sumServiceQuantityInCart } from '@/utils/sumServiceQuantityInCart'
import {
  getBoughtServicesTotal,
  getDaysFromPriceMode,
} from '@/utils/timeSchedules'

interface Props {
  service: Service
}

export const SetupServiceListItem = (props: Props) => {
  const selectedElementQuotedSeatsAmount = useAppSelector(
    activeElementQuotedSeatsAmountSelector
  )
  const dayDifference = useAppSelector(dayDifferenceSelector)
  const selectedElementCartReservation = useAppSelector(
    selectActiveCartReservation
  )
  const selectedElementReservation = useAppSelector(
    cartSlice.selectors.selectedElementReservation
  )
  const quantity = useAppSelector((state) =>
    selectedElementQuotableProductsQuantitySelector(state, props.service)
  )

  const dispatch = useAppDispatch()

  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const serviceInCartQuantity = useMemo(
    () =>
      sumServiceQuantityInCart({
        cartReservations,
        selectedElementCartReservation,
        serviceId: props.service.serviceId,
      }),
    [cartReservations, selectedElementCartReservation, props.service.serviceId]
  )

  function updateQuantity(bought: number) {
    switch (props.service.scheduleType) {
      case 'daily': {
        const days = getDaysFromPriceMode(props.service, dayDifference)
        if (days.length === 0) {
          return
        }

        dispatch(
          cartChangeQuotableServiceQuantity({
            days,
            service: props.service,
            timeSchedules: [
              {
                bought,
                timeSchedule: props.service.timeSchedules[0],
              },
            ],
          })
        )
      }
    }
  }

  function handleAddService() {
    updateQuantity(quantity + 1)
  }

  function handleRemoveService() {
    updateQuantity(quantity - 1)
  }

  const ticketLimit = getServiceSingleLimit({
    dayDifference,
    seatsAmount: selectedElementQuotedSeatsAmount,
    service: props.service,
    serviceInCartQuantity: serviceInCartQuantity,
  })

  const initialQuantity = getBoughtServicesTotal(
    selectedElementReservation?.boughtServices[props.service.serviceId]
  )

  const minimumQuantity = getServiceMinimumQuantity(
    props.service,
    selectedElementQuotedSeatsAmount,
    dayDifference
  )

  const serviceGroups = useAppSelector(
    servicesSlice.selectors.data
  )?.serviceGroups
  const currentServiceGroupName = serviceGroups?.find(
    (group) => group.serviceGroupId === props.service.serviceGroupId
  )?.name

  const message = getServiceLimitReachedMessage({
    groupName: currentServiceGroupName,
    service: props.service,
  })
  const onAddDisabled = () => {
    dispatch(
      toastSlice.actions.show({
        message: message,
      })
    )
  }
  const maxLabel =
    ticketLimit < SERVICE_LABEL_LIMIT
      ? getItemQuantityLabel({
        boundary: ItemQuantityBoundary.MAX,
        flatLimitAmount: props.service.availableQuantity,
        limitType: props.service.maximumLimitType,
      })
      : ' '
  const minLabel = getItemQuantityLabel({
    boundary: ItemQuantityBoundary.MIN,
    flatLimitAmount: props.service.minimumQuantity,
    limitType: props.service.minimumLimitType,
  })

  const available = {
    global:
      props.service.globalAvailableQnt !== undefined
        ? Math.min(
          props.service.globalAvailableQnt ?? 0,
          (props.service.globalMaximumQnt ?? 0) -
              (props.service.globalReservedQnt ?? 0)
        )
        : undefined,
    group:
      props.service.groupAvailableQnt !== undefined
        ? Math.min(
          props.service.groupAvailableQnt ?? 0,
          (props.service.groupMaximumQnt ?? 0) -
              (props.service.groupReservedQnt ?? 0)
        )
        : undefined,
    ticket: ticketLimit,
  }

  if (props.service.timeSchedules.length === 0) {
    return null
  }

  return (
    <ItemQuantityController
      areLimitsReactive
      available={available}
      hasLimit={props.service.hasLimit}
      icon={props.service.icon}
      initialQuantity={initialQuantity}
      key={props.service.serviceId}
      maxLabel={maxLabel}
      minLabel={minLabel}
      minimumQuantity={minimumQuantity}
      name={props.service.name}
      onAdd={handleAddService}
      onAddDisabled={onAddDisabled}
      onRemove={handleRemoveService}
    />
  )
}
